/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/assist/fingerprint.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                    | What Changed          |
|----|------------|-------------------|------------------------|-----------------------|
| 1  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode | Created — builds a    |
|    |            |                   |                        | Fingerprint from a    |
|    |            |                   |                        | resolved SerializedAX |
|    |            |                   |                        | Node for later healing|

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/lib/assist/buildStep.ts
- apps/studio-web/src/lib/assist/__tests__/fingerprint.test.ts

FUNCTIONS / EXPORTS COVERED
----------------------------
- buildFingerprint — (node, path, tree) -> Fingerprint

IMPORTANT POINTS
----------------
1. The consumer of this data is packages/healing's reRankCandidates() at
   Tier-1 heal time — that code reads `fp.screenSignature.split(',')` and
   compares Jaccard overlap against the CURRENT screen's node texts, reads
   `fp.boundsRatio` as [x,y,w,h] normalized 0..1 against the viewport, and
   reads `fp.neighbors.{above,below,left,right}` as plain nearby text.
   This builder must match that CONSUMPTION contract exactly, even though it
   runs against studio-web's SerializedAXNode (browser-side ARIA tree type)
   rather than @plex/dal's A11yNode (packages/healing's normal input) — the
   extension and the runner populate different tree types from the same
   live page, but the Fingerprint's string/number fields are consumer-shape,
   not producer-type-specific, so this still round-trips correctly.
2. AI-Assist-authored steps get a REAL fingerprint from the moment they're
   created — unlike a plain recorded click, which only gets fingerprinted at
   record time. This makes AI Assist steps at least as healable as recorded
   ones, per AI_ASSIST_Research_Plan.md §2 ("more healable than recorded
   clicks" claim) — this file is the code that keeps that claim true.
================================================================================
*/

import type { Fingerprint } from '@plex/ir';
import type { SerializedAXNode, SerializedAXTree } from '../../extension/protocol.js';
import { accessibleName, flattenNodes } from './resolver.js';

function nodeText(node: SerializedAXNode): string {
  return accessibleName(node).toLowerCase().trim();
}

export function buildFingerprint(
  node: SerializedAXNode,
  tree: SerializedAXTree,
): Fingerprint {
  const vw = tree.viewportWidth || 1280;
  const vh = tree.viewportHeight || 800;
  const b  = node.bounds;

  const boundsRatio: [number, number, number, number] = b
    ? [b.x / vw, b.y / vh, b.width / vw, b.height / vh]
    : [0, 0, 0, 0];

  const all = flattenNodes(tree, { interactiveOnly: false });

  // ── screenSignature: comma-joined, de-duplicated, sorted lowercase texts ──
  // Consumed by packages/healing's checkSameScreen() as a Jaccard-comparable set.
  const texts = new Set<string>();
  for (const { node: n } of all) {
    const t = nodeText(n);
    if (t) texts.add(t);
  }
  const screenSignature = Array.from(texts).sort().join(',');

  // ── neighbors: nearest node's text in each cardinal direction ──────────────
  const neighbors: Fingerprint['neighbors'] = {};
  if (b) {
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;

    const scored = all
      .filter(({ node: n }) => n !== node && n.bounds && nodeText(n))
      .map(({ node: n }) => {
        const nb = n.bounds!;
        const ncx = nb.x + nb.width / 2;
        const ncy = nb.y + nb.height / 2;
        return { node: n, ncx, ncy, dx: ncx - cx, dy: ncy - cy };
      });

    const above = scored.filter(s => s.dy < 0 && Math.abs(s.dx) < b.width * 1.5)
      .sort((a, c) => Math.abs(a.dy) - Math.abs(c.dy))[0];
    const below = scored.filter(s => s.dy > 0 && Math.abs(s.dx) < b.width * 1.5)
      .sort((a, c) => Math.abs(a.dy) - Math.abs(c.dy))[0];
    const left = scored.filter(s => s.dx < 0 && Math.abs(s.dy) < b.height * 1.5)
      .sort((a, c) => Math.abs(a.dx) - Math.abs(c.dx))[0];
    const right = scored.filter(s => s.dx > 0 && Math.abs(s.dy) < b.height * 1.5)
      .sort((a, c) => Math.abs(a.dx) - Math.abs(c.dx))[0];

    if (above) neighbors.above = nodeText(above.node);
    if (below) neighbors.below = nodeText(below.node);
    if (left)  neighbors.left  = nodeText(left.node);
    if (right) neighbors.right = nodeText(right.node);
  }

  // ── semanticText: role + name + description + placeholder + testid ────────
  const parts: string[] = [];
  if (node.role) parts.push(node.role);
  if (node.name) parts.push(node.name);
  if (node.description && node.description !== node.name) parts.push(node.description);
  if (node.placeholder) parts.push(node.placeholder);
  const testId = node.attributes?.['data-testid'];
  if (testId) parts.push(testId);
  const semanticText = parts.join(' ').trim() || node.role || 'unknown';

  return { semanticText, boundsRatio, neighbors, screenSignature };
}
